import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Tablet, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { config } from '../../lib/config';
const TIME_API_URL = config.api.time;
/**
 * Kiosk Device Pairing Page
 * PUBLIC ROUTE - No authentication required
 *
 * Allows a shared kiosk device to pair with a tenant using a pairing code
 * generated by an administrator. Once paired, the device receives a long-lived
 * JWT token with only kiosk permissions.
 */
export default function KioskPairPage() {
    const navigate = useNavigate();
    const [pairingCode, setPairingCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const handlePairingCodeChange = (value) => {
        // Auto-format pairing code with hyphen (XXXX-XXXX)
        const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        if (cleaned.length <= 4) {
            setPairingCode(cleaned);
        }
        else {
            setPairingCode(`${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (pairingCode.replace(/-/g, '').length !== 8) {
            setError('Pairing code must be 8 characters');
            return;
        }
        setIsLoading(true);
        try {
            const response = await fetch(`${TIME_API_URL}/kiosk/pair`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    pairingCode: pairingCode,
                }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || error.error || 'Pairing failed');
            }
            const data = await response.json();
            // Store kiosk token in localStorage (safe because token has no privileges)
            localStorage.setItem('lalohr_kiosk_token', data.token);
            localStorage.setItem('lalohr_kiosk_device', JSON.stringify({
                deviceId: data.deviceId,
                tenantId: data.tenantId,
                name: data.name,
                location: data.location,
            }));
            // Redirect to kiosk punch interface
            void navigate('/kiosk');
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to pair device');
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md shadow-lg", children: [_jsxs(CardHeader, { className: "text-center border-b", children: [_jsx("div", { className: "flex justify-center mb-4", children: _jsx("div", { className: "bg-blue-600 p-4 rounded-full", children: _jsx(Tablet, { className: "w-10 h-10 text-white" }) }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: "Pair Kiosk Device" }), _jsx("p", { className: "text-sm text-gray-600 mt-2", children: "Enter the pairing code provided by your administrator" })] }), _jsxs(CardContent, { className: "pt-6", children: [error && (_jsx(Alert, { variant: "destructive", className: "mb-4", children: _jsx(AlertDescription, { children: error }) })), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "pairingCode", className: "block text-sm font-medium mb-1.5", children: "Pairing Code" }), _jsx("input", { id: "pairingCode", type: "text", value: pairingCode, onChange: (e) => handlePairingCodeChange(e.target.value), placeholder: "XXXX-XXXX", className: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg tracking-wider text-center", maxLength: 9, disabled: isLoading, autoFocus: true }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "8-character code (expires in 30 minutes)" })] }), _jsx(Button, { type: "submit", className: "w-full h-12 text-base", disabled: isLoading, children: isLoading ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-5 h-5 mr-2 animate-spin" }), "Pairing..."] })) : ('Pair Device') })] }), _jsx("div", { className: "mt-6 pt-6 border-t", children: _jsxs("div", { className: "text-sm text-gray-600 space-y-2", children: [_jsx("p", { className: "font-medium", children: "Need help?" }), _jsxs("ul", { className: "list-disc list-inside space-y-1 text-xs", children: [_jsx("li", { children: "Ask your administrator for a pairing code" }), _jsx("li", { children: "Codes expire 30 minutes after creation" }), _jsx("li", { children: "Each device requires a unique pairing code" }), _jsx("li", { children: "Contact IT support if you encounter issues" })] })] }) })] })] }) }));
}
